import { useState, useMemo, useCallback } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Calendar as CalendarIcon,
  Clock,
  Star,
  PoundSterling,
  Phone,
  Mail,
  User,
} from 'lucide-react';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import {
  formatPipelineBookingTime,
  getPipelineBooking,
  getPipelineBookingLabel,
} from '@/features/pipeline/utils';

interface Customer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  dealValue: number | null;
  leadScore: number | null;
  createdAt: string | null;
  lastContactDate: string | null;
  nextContactDate: string | null;
  pipelineStageId: number | null;
  additionalData?: Record<string, unknown> | null;
}

interface PipelineStage {
  id: number;
  name: string;
  color: string;
}

interface CalendarPipelineViewProps {
  customers: Customer[];
  stages: PipelineStage[];
  onCustomerClick: (customer: Customer) => void;
}

export function CalendarPipelineView({
  customers,
  stages,
  onCustomerClick,
}: CalendarPipelineViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'added' | 'contact' | 'nextContact'>('added');

  // Memoized function to get customers for a specific date based on view mode
  const getCustomersForDate = useCallback((date: Date) => {
    return customers.filter((customer) => {
      let compareDate: string | null = null;

      switch (viewMode) {
        case 'added':
          compareDate = customer.createdAt;
          break;
        case 'contact':
          compareDate = customer.lastContactDate;
          break;
        case 'nextContact':
          compareDate = customer.nextContactDate;
          break;
      }

      if (!compareDate) return false;
      return isSameDay(new Date(compareDate), date);
    });
  }, [customers, viewMode]);

  // Memoized active dates calculation
  const activeDates = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const daysInMonth = eachDayOfInterval({ start, end });

    return daysInMonth.filter((date) => getCustomersForDate(date).length > 0);
  }, [selectedDate, getCustomersForDate]);

  // Memoized selected date customers
  const selectedDateCustomers = useMemo(() => {
    return getCustomersForDate(selectedDate);
  }, [selectedDate, getCustomersForDate]);

  const getStageForCustomer = useCallback((customer: Customer) => {
    return stages.find((stage) => stage.id === customer.pipelineStageId);
  }, [stages]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar Section */}
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Pipeline Calendar
            </span>
            <div className="flex gap-1">
              <Button
                variant={viewMode === 'added' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('added')}
                className="text-xs"
              >
                Added
              </Button>
              <Button
                variant={viewMode === 'contact' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('contact')}
                className="text-xs"
              >
                Last Contact
              </Button>
              <Button
                variant={viewMode === 'nextContact' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('nextContact')}
                className="text-xs"
              >
                Next Contact
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="rounded-md border transition-none"
            modifiers={{
              hasCustomers: activeDates,
            }}
            modifiersStyles={{
              hasCustomers: {
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                color: 'rgb(34, 197, 94)',
                fontWeight: 'bold',
                transition: 'none',
              },
            }}
            disabled={false}
            fixedWeeks
          />

          {/* Legend */}
          <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="text-sm font-medium mb-2">Calendar Legend</div>
            <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              <div className="w-3 h-3 bg-green-200 rounded"></div>
              <span>
                Days with{' '}
                {viewMode === 'added'
                  ? 'new leads'
                  : viewMode === 'contact'
                    ? 'customer contact'
                    : 'scheduled follow-ups'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Details */}
      <Card className="h-fit">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {format(selectedDate, 'MMMM dd, yyyy')}
            </span>
            <Badge variant="outline">
              {selectedDateCustomers.length} customer{selectedDateCustomers.length !== 1 ? 's' : ''}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDateCustomers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                No{' '}
                {viewMode === 'added'
                  ? 'leads added'
                  : viewMode === 'contact'
                    ? 'customer contact'
                    : 'follow-ups scheduled'}{' '}
                on this date
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedDateCustomers.map((customer) => {
                const stage = getStageForCustomer(customer);
                const booking = getPipelineBooking(customer);
                const bookingTime = booking ? formatPipelineBookingTime(booking) : '';
                const bookingSummary = booking
                  ? `${getPipelineBookingLabel(booking)}${
                      booking.staffName ? ` with ${booking.staffName}` : ''
                    }${bookingTime ? ` at ${bookingTime}` : ''}`
                  : 'Follow-up due';
                return (
                  <div
                    key={customer.id}
                    className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                    onClick={() => onCustomerClick(customer)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-500" />
                        <span className="font-medium text-sm">{customer.name}</span>
                      </div>
                      {stage && (
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{
                            borderColor: stage.color,
                            color: stage.color,
                          }}
                        >
                          {stage.name}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400 mb-2">
                      {customer.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-32">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs">
                        {customer.leadScore && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-500" />
                            <span>{customer.leadScore}/100</span>
                          </div>
                        )}
                        {customer.dealValue && (
                          <div className="flex items-center gap-1">
                            <PoundSterling className="h-3 w-3 text-green-500" />
                            <span>
                              {(customer.dealValue / 100).toLocaleString('en-GB', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      {viewMode === 'nextContact' && customer.nextContactDate && (
                        <div className="text-xs text-slate-500">{bookingSummary}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
